import React, { useCallback, useEffect, useRef, useState } from 'react'
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset,
  IFileAsset
} from '../../types'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { assetDialogEvent, getAssetType } from '../../functions'
import { getBigAssetPath } from '@aytm/lib/common/assets'
import style from './AssetsModal.module.scss'

interface IAssetsModalProps {
  answer: IEcommerceAnswerSchema
  orderedAssetsCollection: assetsCollectionElement[]
  index: number
  onAssetsModalClose: () => void
}

const AssetsModal = ({ answer, orderedAssetsCollection, index, onAssetsModalClose }: IAssetsModalProps) => {
  const timeline = useTimeline()
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  // Mobile has no hover → the lightbox magnifier is desktop-only.
  const allowZoom = !viewport.mobile
  const videoRef = useRef<HTMLVideoElement>(null)
  const playingIndexRef = useRef<number | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number>(index)
  const [isZooming, setIsZooming] = useState(false)
  const [zoomPos, setZoomPos] = useState<{ x: number; y: number } | null>(null)

  const currentAsset = orderedAssetsCollection[selectedIndex]
  const currentType = getAssetType(currentAsset)

  // Log asset change
  useEffect(() => {
    timeline.log({
      type: 'show_asset',
      ref: answer.id,
      target: selectedIndex.toString(),
      // getAssetType returns null for an asset with no usable url — guard before lowercasing.
      context: (getAssetType(orderedAssetsCollection[selectedIndex]) ?? '').toLowerCase()
    })
  }, [selectedIndex, answer.id, orderedAssetsCollection, timeline])

  const logVideoEvent = (type: 'play_video' | 'pause_video', assetIndex: number) =>
    timeline.log({ type, ref: answer.id, target: assetIndex.toString(), context: ASSETS_TYPES.VIDEO.toLowerCase() })

  // The <video> unmounts on asset switch and on close, so its own pause event never reaches React.
  const flushPlayingVideo = () => {
    if (null === playingIndexRef.current) return
    logVideoEvent('pause_video', playingIndexRef.current)
    playingIndexRef.current = null
  }

  // Crossing a type boundary re-frames the dialog: close the outgoing type, open the incoming one
  // (same semantics as a tab switch in the Amazon / Walmart viewers). Same-type moves emit only show_asset.
  const changeAsset = (newIndex: number) => {
    flushPlayingVideo()
    const prev = orderedAssetsCollection[selectedIndex]
    const next = orderedAssetsCollection[newIndex]
    if (getAssetType(prev) !== getAssetType(next)) {
      timeline.log({ ref: answer.id, type: assetDialogEvent('close', prev) })
      timeline.log({ ref: answer.id, type: assetDialogEvent('open', next) })
    }
    setSelectedIndex(newIndex)
  }

  const onPrev = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (selectedIndex > 0) changeAsset(selectedIndex - 1)
  }

  const onNext = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (selectedIndex < orderedAssetsCollection.length - 1) changeAsset(selectedIndex + 1)
  }

  const closeModal = () => {
    flushPlayingVideo()
    timeline.log({ ref: answer.id, type: assetDialogEvent('close', currentAsset) })
    onAssetsModalClose()
  }

  // Hover magnifier for the lightbox image — mirrors ZoomableAsset (scale 2x + mouse-follow pan).
  const onZoomEnter = () => {
    if (!isZooming) {
      timeline.log({ ref: answer.id, target: selectedIndex.toString(), type: 'zoom_image_in' })
      setIsZooming(true)
    }
  }

  const onZoomLeave = () => {
    requestAnimationFrame(() => {
      if (isZooming) {
        timeline.log({ ref: answer.id, target: selectedIndex.toString(), type: 'zoom_image_out' })
      }
      setIsZooming(false)
      setZoomPos(null)
    })
  }

  const onZoomMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isZooming) return
      const element = event.currentTarget
      requestAnimationFrame(() => {
        const { left, top, width, height } = element.getBoundingClientRect()
        const mouseX = event.clientX - left
        const mouseY = event.clientY - top
        setZoomPos({
          x: -(mouseX - width / 2) * 0.75,
          y: -(mouseY - height / 2) * 0.75
        })
      })
    },
    [isZooming]
  )

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  useEffect(
    () => () => flushPlayingVideo(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <div
      className={`${style.overlay} ${viewport.mobile ? style.mobile : ''}`}
      data-testid="SW-EC-AssetsModal"
      onClick={closeModal}
    >
      <button
        type="button"
        className={style.close}
        onClick={event => {
          event.stopPropagation()
          closeModal()
        }}
        aria-label={t('questions', 'ec', 'chewy', 'close')}
      >
        <i className="pi pi-times" />
      </button>

      <div className={style.viewer} onClick={event => event.stopPropagation()}>
        {selectedIndex > 0 && (
          <button
            type="button"
            className={`${style.arrow} ${style.arrowLeft}`}
            onClick={onPrev}
            aria-label={t('questions', 'ec', 'chewy', 'previous')}
          >
            <i className="pi pi-angle-left" />
          </button>
        )}

        {ASSETS_TYPES.IMAGE === currentType && (
          <div
            className={style.imageZoom}
            onMouseEnter={allowZoom ? onZoomEnter : null}
            onMouseLeave={allowZoom ? onZoomLeave : null}
            onMouseMove={allowZoom ? onZoomMove : null}
          >
            <div
              className={style.image}
              style={{
                backgroundImage: `url(${getBigAssetPath(currentAsset as IImageAsset)})`,
                backgroundPosition:
                  allowZoom && zoomPos ? `calc(50% + ${zoomPos.x}px) calc(50% + ${zoomPos.y}px)` : '50% 50%',
                transform: `scale(${allowZoom && isZooming ? 2 : 1})`,
                cursor: allowZoom ? (isZooming ? 'zoom-out' : 'zoom-in') : 'default'
              }}
            />
          </div>
        )}

        {ASSETS_TYPES.VIDEO === currentType && (
          <video
            ref={videoRef}
            playsInline
            autoPlay
            controls
            className={style.video}
            src={(currentAsset as IEcommerceVideoAsset).video.url}
            preload="metadata"
            onPlay={() => {
              playingIndexRef.current = selectedIndex
              logVideoEvent('play_video', selectedIndex)
            }}
            onPause={() => {
              playingIndexRef.current = null
              logVideoEvent('pause_video', selectedIndex)
            }}
          />
        )}

        {ASSETS_TYPES.MODEL === currentType && (
          <div className={style.model}>
            <model-viewer
              src={(currentAsset as IFileAsset).file_url}
              camera-controls={true}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        )}

        {selectedIndex < orderedAssetsCollection.length - 1 && (
          <button
            type="button"
            className={`${style.arrow} ${style.arrowRight}`}
            onClick={onNext}
            aria-label={t('questions', 'ec', 'chewy', 'next')}
          >
            <i className="pi pi-angle-right" />
          </button>
        )}
      </div>
    </div>
  )
}

export default AssetsModal
